import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import './InvigilatorSessions.css'
import LoadingScreen from '../components/LoadingScreen'

const InvigilatorSessions = () => {
  const [sessions, setSessions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const history = useHistory()

  useEffect(() => {
    let isMounted = true

    const fetchSessions = async () => {
      try {
        const token = localStorage.getItem('access_token')
        const response = await fetch('/api/invigilator/active-sessions', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.ok) {
          const data = await response.json()
          if (isMounted) {
            setSessions(data.sessions || [])
          }
        } else {
          if (isMounted) {
            setError('Failed to load sessions')
          }
        }
      } catch (err) {
        if (isMounted) {
          setError('Network error')
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchSessions()
    const interval = setInterval(() => {
      if (isMounted) {
        fetchSessions()
      }
    }, 10000) // Refresh every 10 seconds

    return () => {
      isMounted = false
      clearInterval(interval)
    }
  }, [])

  const handleMonitor = (attemptId) => {
    history.push(`/invigilator/attempt/${attemptId}`)
  }

  if (loading) {
    return <LoadingScreen message="Loading Active Sessions" />
  }

  return (
    <div className="sessions-container">
      <header className="page-header">
        <div>
          <h1>Active Test Sessions</h1>
          <p className="subtitle">Real-time monitoring of ongoing tests</p>
        </div>
        <button onClick={() => history.push('/invigilator-dashboard')} className="back-button">
          Back to Dashboard
        </button>
      </header>

      {error && <div className="error-message">{error}</div>}

      <div className="sessions-stats">
        <div className="stat-card">
          <span className="stat-number">{sessions.length}</span>
          <span className="stat-label">Active Sessions</span>
        </div>
        <div className="stat-card warning">
          <span className="stat-number">
            {sessions.filter(s => s.violation_count > 0).length}
          </span>
          <span className="stat-label">With Violations</span>
        </div>
        <div className="stat-card success">
          <span className="stat-number">
            {sessions.filter(s => s.violation_count === 0).length}
          </span>
          <span className="stat-label">Clean Sessions</span>
        </div>
      </div>

      <div className="sessions-content">
        {sessions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">👁️</div>
            <h3>No Active Sessions</h3>
            <p>There are currently no students taking tests</p>
          </div>
        ) : (
          <div className="sessions-grid">
            {sessions.map((session) => (
              <div
                key={session.id}
                className={`session-card ${session.violation_count > 0 ? 'flagged' : ''}`}
              >
                <div className="session-header">
                  <div className="student-info">
                    <h3>{session.student_name}</h3>
                    <p className="student-email">{session.student_email}</p>
                  </div>
                  <div className="live-indicator">
                    <span className="live-dot"></span>
                    LIVE
                  </div>
                </div>

                <div className="test-info">
                  <h4>{session.test_title}</h4>
                  <div className="time-elapsed">
                    Time: {Math.floor(session.time_elapsed / 60)}:{String(session.time_elapsed % 60).padStart(2, '0')}
                  </div>
                </div>

                <div className="violation-info">
                  <div className={`violation-count ${session.violation_count > 0 ? 'has-violations' : ''}`}>
                    <span className="violation-icon">⚠️</span>
                    <span>{session.violation_count || 0} Violations</span>
                  </div>
                  {session.last_violation && (
                    <div className="last-violation">
                      Last: {session.last_violation}
                    </div>
                  )}
                </div>

                <button
                  onClick={() => handleMonitor(session.id)}
                  className={`monitor-btn ${session.violation_count > 0 ? 'urgent' : ''}`}
                >
                  Monitor Session
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default InvigilatorSessions
